import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { 
  ShieldCheck, 
  Lock, 
  Mail, 
  KeyRound, 
  Eye, 
  EyeOff, 
  Store, 
  Sparkles, 
  ArrowRight, 
  CheckCircle2,
  AlertCircle,
  Key
} from 'lucide-react';

export default function AdminLoginModal() {
  const { loginAdmin, setIsAdminMode, storeSettings } = useShop();
  const [loginMethod, setLoginMethod] = useState('pin'); // 'pin' or 'credentials'
  const [pin, setPin] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutTimer, setLockoutTimer] = useState(0);

  // Lockout countdown timer
  React.useEffect(() => {
    let interval = null;
    if (lockoutTimer > 0) {
      interval = setInterval(() => {
        setLockoutTimer((t) => t - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [lockoutTimer]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (lockoutTimer > 0) return;
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      let res;
      if (loginMethod === 'pin') {
        const cleanPin = (pin || '').trim();
        if (!cleanPin) {
          setErrorMessage('Please enter the Administrator PIN');
          setIsSubmitting(false);
          return;
        }
        res = await loginAdmin(cleanPin, cleanPin);
      } else {
        const cleanUser = (username || '').trim();
        const cleanPass = (password || '').trim();
        if (!cleanUser || !cleanPass) {
          setErrorMessage('Please enter both admin email and password');
          setIsSubmitting(false);
          return;
        }
        res = await loginAdmin(cleanUser, cleanPass);
      }

      setIsSubmitting(false);
      if (!res || !res.success) {
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        if (newAttempts >= 5) {
          setLockoutTimer(60);
          setErrorMessage('Too many failed attempts. Security lockout active for 60 seconds.');
        } else {
          setErrorMessage(res?.error || 'Access Denied: Invalid security credentials.');
        }
      } else {
        setFailedAttempts(0);
      }
    } catch (err) {
      setIsSubmitting(false);
      setErrorMessage(err.message || 'Authentication error. Access Denied.');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.65)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1.5rem'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '460px',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Top Flipkart Blue Header Strip */}
        <div style={{
          backgroundColor: '#2874f0',
          padding: '1.5rem 1.8rem',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '42px',
            height: '42px',
            borderRadius: '4px',
            background: '#ffe500',
            color: '#2874f0',
            fontWeight: 900,
            fontSize: '1.3rem',
            fontStyle: 'italic',
            marginBottom: '0.6rem',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
          }}>
            ML
          </div>
          <h2 style={{
            fontSize: '1.35rem',
            fontWeight: 800,
            fontStyle: 'italic',
            color: '#ffffff',
            marginBottom: '0.2rem'
          }}>
            M.L. JEWELLERS SELLER HUB
          </h2>
          <p style={{ fontSize: '0.82rem', color: '#ffe500', fontWeight: 600 }}>
            Admin Portal & Vault Command Center
          </p>
        </div>

        <div style={{ padding: '1.8rem 1.8rem' }}>
          
          {/* Login Method Toggle */}
          <div style={{
            display: 'flex',
            background: '#f1f3f6',
            padding: '4px',
            borderRadius: '2px',
            marginBottom: '1.4rem',
            border: '1px solid #e0e0e0'
          }}>
            <button
              type="button"
              onClick={() => { setLoginMethod('pin'); setErrorMessage(''); }}
              style={{
                flex: 1,
                padding: '0.55rem',
                border: 'none',
                borderRadius: '2px',
                background: loginMethod === 'pin' ? '#2874f0' : 'transparent',
                color: loginMethod === 'pin' ? '#ffffff' : '#424242',
                fontWeight: loginMethod === 'pin' ? 700 : 600,
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem',
                transition: 'all 0.15s ease'
              }}
            >
              <Key size={14} /> Security PIN (Fast)
            </button>
            <button
              type="button"
              onClick={() => { setLoginMethod('credentials'); setErrorMessage(''); }}
              style={{
                flex: 1,
                padding: '0.55rem',
                border: 'none',
                borderRadius: '2px',
                background: loginMethod === 'credentials' ? '#2874f0' : 'transparent',
                color: loginMethod === 'credentials' ? '#ffffff' : '#424242',
                fontWeight: loginMethod === 'credentials' ? 700 : 600,
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem',
                transition: 'all 0.15s ease'
              }}
            >
              <Mail size={14} /> Email & Password
            </button>
          </div>

          {/* Error Banner */}
          {errorMessage && (
            <div style={{
              background: '#ffebee',
              border: '1px solid #ffcdd2',
              borderRadius: '2px',
              padding: '0.75rem 1rem',
              marginBottom: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              fontSize: '0.82rem',
              color: '#c62828'
            }}>
              <AlertCircle size={16} color="#c62828" style={{ flexShrink: 0 }} />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            
            {loginMethod === 'pin' ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <label style={{
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: '#424242',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }}>
                    Administrator PIN (Default: 1234)
                  </label>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={10}
                    autoFocus
                    required
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="Enter PIN (e.g. 1234)"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem 0.75rem 2.5rem',
                      fontSize: '1.1rem',
                      letterSpacing: '0.2em',
                      fontWeight: 800,
                      backgroundColor: '#ffffff',
                      border: '1px solid #dcdcdc',
                      borderRadius: '2px',
                      color: '#212121',
                      outline: 'none'
                    }}
                  />
                  <KeyRound size={18} color="#2874f0" style={{
                    position: 'absolute',
                    left: '0.9rem',
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }} />
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: '#424242',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: '0.4rem'
                  }}>
                    Admin Email / Username
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="admin@mljewellers.com"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem 0.75rem 2.5rem',
                        fontSize: '0.88rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #dcdcdc',
                        borderRadius: '2px',
                        color: '#212121',
                        outline: 'none'
                      }}
                    />
                    <Mail size={16} color="#757575" style={{
                      position: 'absolute',
                      left: '0.9rem',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                    <label style={{
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      color: '#424242',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em'
                    }}>
                      Password
                    </label>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      style={{
                        width: '100%',
                        padding: '0.75rem 2.5rem 0.75rem 2.5rem',
                        fontSize: '0.88rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #dcdcdc',
                        borderRadius: '2px',
                        color: '#212121',
                        outline: 'none'
                      }}
                    />
                    <KeyRound size={16} color="#757575" style={{
                      position: 'absolute',
                      left: '0.9rem',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }} />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '0.9rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#757575',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Login Button - Flipkart Orange */}
            <button
              type="submit"
              disabled={isSubmitting || lockoutTimer > 0}
              style={{
                width: '100%',
                padding: '0.85rem',
                fontSize: '0.92rem',
                fontWeight: 700,
                marginTop: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                backgroundColor: '#fb641b',
                color: '#ffffff',
                border: 'none',
                borderRadius: '2px',
                boxShadow: '0 1px 2px 0 rgba(0,0,0,0.2)',
                opacity: lockoutTimer > 0 ? 0.6 : 1,
                cursor: lockoutTimer > 0 ? 'not-allowed' : 'pointer',
                letterSpacing: '0.5px'
              }}
            >
              <Lock size={16} />
              <span>
                {lockoutTimer > 0
                  ? `Security Lockout (${lockoutTimer}s)`
                  : isSubmitting
                  ? 'Authenticating...'
                  : 'LOGIN TO SELLER HUB'}
              </span>
              <ArrowRight size={16} />
            </button>
          </form>

          {/* Quick Credential Hint */}
          <div style={{
            marginTop: '1rem',
            padding: '0.6rem 0.8rem',
            backgroundColor: '#e8f0fe',
            border: '1px solid #d2e3fc',
            borderRadius: '2px',
            fontSize: '0.78rem',
            color: '#1a73e8',
            textAlign: 'center'
          }}>
            💡 Quick Access: Enter PIN <strong>1234</strong> to unlock Seller Hub
          </div>

          {/* Return to Storefront */}
          <div style={{
            marginTop: '1.2rem',
            paddingTop: '1rem',
            borderTop: '1px solid #f0f0f0',
            textAlign: 'center'
          }}>
            <button
              type="button"
              onClick={() => {
                setIsAdminMode(false);
                if (typeof window !== 'undefined') {
                  window.location.hash = '#/';
                }
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#2874f0',
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <Store size={14} />
              <span>Return to M.L. Jewellers Storefront</span>
            </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
